import { useEffect, useRef, useCallback } from 'react';

interface UseIdleTimeoutOptions {
  onIdle: () => void;
  idleTime: number; // en millisecondes
  enabled?: boolean;
}

/**
 * Hook pour détecter l'inactivité de l'utilisateur et déclencher une action
 *
 * @param onIdle - Fonction appelée après la période d'inactivité
 * @param idleTime - Durée d'inactivité en millisecondes (ex: 600000 pour 10 min)
 * @param enabled - Active ou désactive le suivi d'inactivité
 */
export const useIdleTimeout = ({ onIdle, idleTime, enabled = true }: UseIdleTimeoutOptions) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const idleCallbackRef = useRef(onIdle);

  // Mettre à jour la référence du callback
  useEffect(() => {
    idleCallbackRef.current = onIdle;
  }, [onIdle]);

  const resetTimer = useCallback(() => {
    // Nettoyer le timer existant
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Créer un nouveau timer
    if (enabled) {
      timeoutRef.current = setTimeout(() => {
        idleCallbackRef.current();
      }, idleTime);
    }
  }, [idleTime, enabled]);

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    // Événements à surveiller pour détecter l'activité
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Initialiser le timer
    resetTimer();

    // Ajouter les écouteurs d'événements
    events.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    // Nettoyer lors du démontage
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
    };
  }, [resetTimer, enabled]);

  // Fonction pour réinitialiser manuellement le timer
  const reset = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  return { reset };
};
